import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import useDelayedUnmount from "../../../hooks/useDelayedUnmount";

/**
 * 全屏图片预览
 * image = { url, title } 或 null
 */
const PreviewModal = ({ image, onClose, onPrev, onNext, hasPrev = false, hasNext = false }) => {
  const visibility = useDelayedUnmount(Boolean(image), 140);
  const [displayImage, setDisplayImage] = React.useState(image);

  React.useEffect(() => {
    if (image) setDisplayImage(image);
  }, [image]);

  React.useEffect(() => {
    if (!image) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowLeft" && hasPrev) onPrev?.();
      if (event.key === "ArrowRight" && hasNext) onNext?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [hasNext, hasPrev, image, onClose, onNext, onPrev]);

  if (!visibility.mounted || !displayImage) return null;
  return (
    <div
      className={`fixed inset-0 z-50 p-4 flex items-center justify-center ${visibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
      style={{ backgroundColor: "var(--overlay-heavy)" }}
      onClick={onClose}
    >
      <div
        className={`w-full h-full max-w-[min(96vw,1800px)] max-h-[94vh] flex flex-col gap-3 ${visibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold truncate" style={{ color: "#fff" }}>{displayImage.title}</div>
          <button
            type="button"
            onClick={onClose}
            className="ui-pressable h-9 w-9 rounded-md border inline-flex items-center justify-center cursor-pointer"
            style={{ borderColor: "rgba(255,255,255,0.3)", backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" }}
            aria-label="关闭预览"
          >
            <X size={18} />
          </button>
        </div>
        <div className="relative min-h-0 flex-1 rounded-md overflow-auto custom-scrollbar" /* 灯箱媒体区底色：深色媒体背景，与主题无关，保留 */ style={{ backgroundColor: "#050505" }}>
          {hasPrev && (
            <button
              type="button"
              onClick={onPrev}
              className="ui-pressable absolute left-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border inline-flex items-center justify-center cursor-pointer"
              style={{ borderColor: "rgba(255,255,255,0.28)", backgroundColor: "rgba(0,0,0,0.52)", color: "#fff" }}
              aria-label="上一张故事板"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          {hasNext && (
            <button
              type="button"
              onClick={onNext}
              className="ui-pressable absolute right-3 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full border inline-flex items-center justify-center cursor-pointer"
              style={{ borderColor: "rgba(255,255,255,0.28)", backgroundColor: "rgba(0,0,0,0.52)", color: "#fff" }}
              aria-label="下一张故事板"
            >
              <ChevronRight size={24} />
            </button>
          )}
          <img src={displayImage.url} alt={displayImage.title} className="w-full h-auto object-contain" />
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
